import { GridColDef } from '@mui/x-data-grid';

import { SectionPermissions } from '../Shared/PermissionAccessGuard/hooks/useSectionPermissions';
import { getAgreementColumns } from './AgreementColumn';
import { getAssetsColumns } from './AssetsColumn';
import { getCaseColumns } from './CaseColumn';
import { roleDescriptionColumn } from './RoleDescriptionColumn';
import { getSiteColumns } from './SiteColumn';
import { getSubscriptionColumn } from "./SubscriptionColumn";
import { getUserManagementColumn } from "./UserManagementColumns";

type LegendItem = { color: string; label: string };
interface SectionSetup {
  columns: GridColDef[];
  legend: LegendItem[];
}

const legendsBySection: Record<string, { color: string; label: string }[]> = {
  agreements: [
    { color: '#27ae60', label: 'Active' },
    { color: '#f39c12', label: 'Warning' },
    { color: '#c0392b', label: 'Inactive' },
  ],
  cases: [
    { color: '#27ae60', label: 'Normal' },
    { color: '#f39c12', label: 'Medium' },
    { color: '#c0392b', label: 'High' },
    { color: '#0e0808', label: 'Critical' },
  ],
  agreementsBySite: [
    { color: '#27ae60', label: 'Active' },
    { color: '#f39c12', label: 'Warning' },
    { color: '#c0392b', label: 'Inactive' },
  ],
  relatedCases: [
    { color: '#27ae60', label: 'Normal' },
    { color: '#f39c12', label: 'Medium' },
    { color: '#c0392b', label: 'High' },
    { color: '#0e0808', label: 'Critical' },
  ],
  assets: [
    { color: '#28a745', label: 'Active' },
    { color: '#0079D0', label: 'Classic' },
    { color: '#dc3545', label: 'Limited' },
    { color: '#000000', label: 'Obsolete' },
  ],
};
const getAssetSectionSetup = (_productCategory: string | undefined, viewPageUrl?: string, viewLabel?: string): SectionSetup => {
  return { columns: getAssetsColumns(viewPageUrl, viewLabel), legend: legendsBySection.assets };
};

type SectionSetupOptions = {
  sectionType: string | undefined;
  viewPageUrl: string | undefined;
  productCategory: string | undefined;
  permissions: SectionPermissions;
  viewLabel?: string;
  subscriptionActions?: { buttonType: string; buttonLabel: string; buttonUrl?: string }[];
  onDeleteClick?: (id: string) => void;
  actionLabel?: string;
};

export const getSectionSetup = ({
  sectionType,
  viewPageUrl,
  productCategory,
  permissions,
  viewLabel,
  subscriptionActions = [],
  onDeleteClick,
  actionLabel,
}: SectionSetupOptions): SectionSetup => {
  switch (sectionType) {
    case 'agreements':
    case 'agreementsBySite':
      return {
        columns: getAgreementColumns({ view: viewLabel }, viewPageUrl, permissions.canViewAgreementsViewDetails),
        legend: legendsBySection.agreements,
      };
    case 'cases':
      return {
        columns: getCaseColumns({ view: viewLabel }, viewPageUrl, permissions.canViewCasesDetails),
        legend: legendsBySection.cases,
      };
    case 'relatedSites':
      return {
        columns: getSiteColumns({}, viewPageUrl, permissions.canViewSitesView),
        legend: [],
      };
    case 'relatedCases':
      return {
        columns: getCaseColumns({ view: viewLabel }, viewPageUrl, permissions.canViewCasesDetails),
        legend: legendsBySection.relatedCases,
      };
    case 'assets':
      return getAssetSectionSetup(productCategory, viewPageUrl, viewLabel);
    case 'subscriptions':
      return { columns: getSubscriptionColumn(subscriptionActions, onDeleteClick, actionLabel), legend: [] };
    case 'userManagement':
      return { columns: getUserManagementColumn(subscriptionActions, onDeleteClick, actionLabel), legend: [] };
    case 'userManagementRoles':
      return { columns: roleDescriptionColumn(), legend: [] };
    default:
      return {
        columns: [],
        legend: [],
      };
  }
};
